import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';
import { LoginRequest } from '@/models/auth/login-request.model';
import { LoginResponse } from '@/models/auth/login-response.model';
import { UsuarioResponse } from '@/models/usuario.model';
import { EnvironmentConfig } from '@/configs/environment.config';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly API_URL = inject(EnvironmentConfig).getApiUrl();

    private readonly tokenSignal = signal<string | null>(this.getStoredToken());
    private readonly currentUserSignal = signal<UsuarioResponse | null>(this.getStoredUser());
    public readonly token = this.tokenSignal.asReadonly();
    public readonly currentUser = this.currentUserSignal.asReadonly();
    public readonly isAuthenticated = computed(() => this.checkAuthentication());

    initializeUser(): void {
        if (this.getToken() && !this.currentUserSignal()) {
            this.http.get<UsuarioResponse>(`${this.API_URL}/usuarios/perfil`).subscribe({
                next: (user) => this.setCurrentUser(user),
                error: () => this.logout()
            });
        }
    }

    login(credentials: LoginRequest): Observable<LoginResponse> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        return this.http.post<LoginResponse>(`${this.API_URL}/auth/login`, credentials, { headers }).pipe(
            tap((response) => {
                this.storeToken(response.token, response.expiresIn, response.nome);
                this.tokenSignal.set(response.token);
            }),
            switchMap((response) =>
                this.http.get<UsuarioResponse>(`${this.API_URL}/usuarios/perfil`).pipe(
                    tap((user) => this.setCurrentUser(user)),
                    map(() => response)
                )
            )
        );
    }

    logout(): void {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token_expiration');
        localStorage.removeItem('current_user');
        localStorage.removeItem('nome');
        this.tokenSignal.set(null);
        this.currentUserSignal.set(null);
    }

    setCurrentUser(user: UsuarioResponse): void {
        this.currentUserSignal.set(user);
        localStorage.setItem('current_user', JSON.stringify(user));
    }

    getToken(): string | null {
        return this.tokenSignal();
    }

    private checkAuthentication(): boolean {
        const token = this.tokenSignal();

        if (!token) {
            return false;
        }

        const expiration = localStorage.getItem('token_expiration');

        if (expiration) {
            const expirationDate = new Date(parseInt(expiration));

            return new Date() < expirationDate;
        }

        return false;
    }

    private storeToken(token: string, expiresIn: number, nome: string): void {
        localStorage.setItem('auth_token', token);
        localStorage.setItem('nome', nome);

        const expirationDate = new Date().getTime() + expiresIn;

        localStorage.setItem('token_expiration', expirationDate.toString());
    }

    private getStoredToken(): string | null {
        return localStorage.getItem('auth_token');
    }

    private getStoredUser(): UsuarioResponse | null {
        const userJson = localStorage.getItem('current_user');

        if (userJson) {
            try {
                return JSON.parse(userJson);
            } catch {
                return null;
            }
        }

        return null;
    }
}
